import { Injectable, Signal, computed, effect, inject, signal } from '@angular/core';
import { Store } from '@ngrx/store';

import { selectPlanChanges } from '@core/store/plan-edit/plan-edit-session.selectors';
import { selectPlan } from '@core/store/plan/plan.selectors';
import { UserPreferencesActions } from '@core/store/user-preferences/user-preferences.actions';
import { PlanPanelPreferences } from '@core/store/user-preferences/user-preferences.model';
import {
  selectPlanPanelCollapsedSections,
  selectPlanPanelOpen
} from '@core/store/user-preferences/user-preferences.selectors';

import { ImpactReference } from './sections/kpis/impact-kpis';

/** Whether the plan panel is open, how wide it is, and which of its sections are collapsed. */
@Injectable({ providedIn: 'root' })
export class PlanPanelService {
  private readonly store = inject(Store);
  private readonly collapsedSections = this.store.selectSignal(
    selectPlanPanelCollapsedSections
  );
  private readonly pendingReveal = signal<{ sectionId: string; seq: number } | null>(null);
  private revealSeq = 0;

  readonly open = this.store.selectSignal(selectPlanPanelOpen);
  private readonly plan = this.store.selectSignal(selectPlan);
  /** The panel and its toggle exist only while a plan is in view. */
  readonly planInView = computed(() => this.plan() != null);
  /** Not saved: a reload starts collapsed. */
  readonly expanded = signal(false);
  readonly kpiReference = signal<ImpactReference>('saved');
  private readonly changes = this.store.selectSignal(selectPlanChanges);
  /** Whether the plan's live session has a change for the KPIs to be compared with. */
  readonly kpiHasRound = computed(() => !!this.changes()?.lastRound);
  /** The chosen reference, or the saved plan while there is no change to compare with. */
  readonly effectiveKpiReference = computed<ImpactReference>(() =>
    this.kpiHasRound() ? this.kpiReference() : 'saved'
  );
  readonly revealRequest = this.pendingReveal.asReadonly();

  constructor() {
    // The panel unmounts with the plan, and the next plan must not open expanded or scroll to
    // a section nobody asked it for.
    effect(() => {
      if (this.planInView()) return;
      this.expanded.set(false);
      this.pendingReveal.set(null);
    });
  }

  toggle(): void {
    const opening = !this.open();
    if (!opening) this.expanded.set(false);
    this.save({ open: opening });
  }

  toggleExpanded(): void {
    this.expanded.update((expanded) => !expanded);
  }

  isCollapsed(id: string): Signal<boolean> {
    return computed(() => this.collapsedSections().includes(id));
  }

  toggleSection(id: string): void {
    const collapsed = this.collapsedSections();
    this.save({
      collapsedSections: collapsed.includes(id)
        ? collapsed.filter((other) => other !== id)
        : [...collapsed, id]
    });
  }

  /** Opens the panel, uncollapses `sectionId` and asks the panel to scroll it into view. */
  reveal(sectionId: string): void {
    const changes: Partial<PlanPanelPreferences> = {};
    if (!this.open()) changes.open = true;
    const collapsed = this.collapsedSections();
    if (collapsed.includes(sectionId))
      changes.collapsedSections = collapsed.filter((other) => other !== sectionId);
    if (Object.keys(changes).length > 0) this.save(changes);
    this.pendingReveal.set({ sectionId, seq: ++this.revealSeq });
  }

  /**
   * Called by the panel once it has scrolled to the request, or dropped it for a section it does
   * not show, so a later reopening or a section appearing does not scroll unasked.
   */
  revealHandled(seq: number): void {
    if (this.pendingReveal()?.seq === seq) this.pendingReveal.set(null);
  }

  private save(preferences: Partial<PlanPanelPreferences>): void {
    this.store.dispatch(UserPreferencesActions.setPlanPanelPreferences({ preferences }));
  }
}
